import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './dataEntry.css'
import { useLanguage } from '../../context/LanguageContext'
import translations from '../../translations.json'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'

const Field = ({ label, name, type = 'text', value, onChange, error, errorMsg }) => (
  <div className="viGuard__dataEntry-field">
    <label htmlFor={name}>{label}</label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      className={error ? 'error' : ''}
    />
    {error && <span className="error-msg">— {errorMsg}</span>}
  </div>
)

const DataEntry = () => {
  const {
    language,
    setVisitorData,
    setSessionId,
    tenantSlug,
    setAlreadyCheckedIn,
    setQrBase64,
    resetSession,
    startSession,
    getNextScreen,
  } = useLanguage()

  const t        = translations[language] || translations['en']
  const navigate = useNavigate()

  const [checking, setChecking] = useState(false)
  const [existing, setExisting] = useState(null)
  const [errors,   setErrors]   = useState({})
  const [form, setForm] = useState({
    firstName:    '',
    lastName:     '',
    company:      '',
    email:        '',
    phone:        '',
    visitDate:    '',
    licensePlate: '',
  })

  useEffect(() => {
    resetSession()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: false }))
  }

  const validate = () => {
    const e = {}
    if (!form.firstName.trim()) e.firstName = true
    if (!form.lastName.trim())  e.lastName  = true
    if (!form.company.trim())   e.company   = true
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true
    if (!form.phone.trim())     e.phone     = true
    if (!form.visitDate)        e.visitDate = true
    // licensePlate is optional — no validation needed
    return e
  }

  const handleSubmit = async () => {
    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    if (!tenantSlug) {
      setErrors({ _general: true })
      console.error('No tenant slug in URL — add ?tenant=yourslug to the URL')
      return
    }

    setChecking(true)

    try {
      console.log('=== CHECK-EXISTING DEBUG ===')
      console.log('tenantSlug in context:', tenantSlug)
      console.log('email:', form.email)
      console.log('visitDate:', form.visitDate)

      const response = await fetch(`${BACKEND_URL}/api/checkin/check-existing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Slug': tenantSlug,
        },
        body: JSON.stringify({ email: form.email, visitDate: form.visitDate }),
      })

      const data = await response.json()

      console.log('HTTP status:', response.status)
      console.log('Response:', JSON.stringify(data))
      console.log('===========================')

      if (data.exists) {
        setExisting(data)
        setSessionId(data.sessionId)
        setQrBase64(data.qrBase64)
        setAlreadyCheckedIn(true)
      } else {
        setVisitorData(form)
        startSession()
        navigate(getNextScreen('data_entry'))
      }

    } catch (err) {
      console.error('Existing check failed:', err)
      setVisitorData(form)
      startSession()
      navigate(getNextScreen('data_entry'))
    } finally {
      setChecking(false)
    }
  }

  if (existing) {
    const name    = `${existing.firstName} ${existing.lastName}`
    const message = t.existing_checkin_message
      .replace('{name}', name)
      .replace('{date}', form.visitDate)

    return (
      <div className="viGuard__dataEntry">
        <div className="viGuard__dataEntry-content">
          <div className="viGuard__dataEntry-confirm">
            <div className="checkmark">!</div>
            <h2 className="gradient__text">{t.existing_checkin_title}</h2>
            <p>{message}</p>
            <button onClick={() => navigate('/QRCode?existing=true')}>
              {t.existing_checkin_button}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="viGuard__dataEntry">
      <div className="viGuard__dataEntry-content">
        <h1 className="gradient__text">{t.data_entry_title}</h1>
        <p className="subtitle">{t.data_entry_subtitle}</p>

        {errors._general && (
          <div className="de-general-error">
            {t.no_tenant_error}
          </div>
        )}

        <div className="viGuard__dataEntry-form">

          <div className="viGuard__dataEntry-row">
            <Field
              label={t.first_name} name="firstName" value={form.firstName}
              onChange={handleChange} error={errors.firstName} errorMsg={t.required}
            />
            <Field
              label={t.last_name} name="lastName" value={form.lastName}
              onChange={handleChange} error={errors.lastName} errorMsg={t.required}
            />
          </div>

          <Field
            label={t.company} name="company" value={form.company}
            onChange={handleChange} error={errors.company} errorMsg={t.required}
          />

          <Field
            label={t.email} name="email" type="email" value={form.email}
            onChange={handleChange} error={errors.email} errorMsg={t.required}
          />

          <div className="viGuard__dataEntry-row">
            <Field
              label={t.phone} name="phone" type="tel" value={form.phone}
              onChange={handleChange} error={errors.phone} errorMsg={t.required}
            />
            <Field
              label={t.visit_date} name="visitDate" type="date" value={form.visitDate}
              onChange={handleChange} error={errors.visitDate} errorMsg={t.required}
            />
          </div>

          <Field
            label={`${t.license_plate} (${t.optional})`} name="licensePlate" value={form.licensePlate}
            onChange={handleChange}
          />

          <div className="viGuard__dataEntry-submit">
            <button type="button" onClick={handleSubmit} disabled={checking}>
              {checking ? t.checking : t.submit_button}
            </button>
          </div>

        </div>
      </div>
    </div>
  )
}

export default DataEntry
